import React, {
  CSSProperties, useState, useEffect, useCallback, useRef,
} from 'react'
import {
  GoogleMap,
  MarkerClusterer,
  useJsApiLoader,
} from '@react-google-maps/api'
import type { Libraries } from '@googlemaps/js-api-loader'
import { Clusterer, type ClusterIconStyle } from '@react-google-maps/marker-clusterer'
import { GOOGLE_MAP_API_KEY, GOOGLE_MAP_CLUSTER_MAX_ZOOM } from '../../constants/Constants'
import { getFromNowAsText } from '../../helpers/timeHelper'
import {
  calculateMamorioClusterIcon,
  groupMamoriosByPosition,
} from '../../helpers/mapMarkerHelper'
import incrementGoogleMapsLoadCount from '../../utils/googleMapsLoadCounter'
import MamorioMarker from '../common/MamorioMarker'
import OverlappingMamoriosMarker from '../common/OverlappingMamoriosMarker'
import './rental-label.css'

type Props = {
  zoom: number
  mamoriosOrDevices: IMamorio[]
  containerStyle: CSSProperties
}

type Position = {
  lat: number
  lng: number
}

const clusterStyles: ClusterIconStyle[] = [{
  url: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m1.png',
  height: 53,
  width: 53,
  textColor: '#000000',
}]

const GoogleMapWrapperForMamoriosView = ({
  zoom,
  mamoriosOrDevices,
  containerStyle,
}: Props) => {
  const [libraries] = useState<Libraries>(['visualization'])
  const {
    isLoaded,
    loadError,
  } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
    libraries,
  })
  const hasCountedLoadRef = useRef(false)

  const [size, setSize] = useState<undefined | google.maps.Size>(undefined)
  const [selectedMamorio, setSelectedMamorio] = useState<IMamorio | null>(null)
  const [showInfoWindow, setShowInfoWindow] = useState<boolean>(false) // InfoWindowの表示状態を管理
  const [openOverlappingPositionKey, setOpenOverlappingPositionKey] = useState<string | null>(null)
  const [mapCenter, setMapCenter] = useState<Position>({
    lat: Number(35.7007505),
    lng: Number(139.769443),
  })

  useEffect(() => {
    // MAMORIOのフィルターの変更が行われたらデセレクトする
    setSelectedMamorio(null)
    setShowInfoWindow(false)
    setOpenOverlappingPositionKey(null)
  }, [mamoriosOrDevices])

  useEffect(() => {
    if (!isLoaded || hasCountedLoadRef.current) {
      return
    }
    incrementGoogleMapsLoadCount('GoogleMapWrapperForMamoriosView')
    hasCountedLoadRef.current = true
  }, [isLoaded])

  useEffect(() => {
    if (selectedMamorio && selectedMamorio.recent_log) {
      setMapCenter({ lat: Number(selectedMamorio.recent_log.latitude), lng: Number(selectedMamorio.recent_log.longitude) })
    } else if (mamoriosOrDevices.length > 0) {
      const recentLog = mamoriosOrDevices.filter((m) => m.recent_log)[0]?.recent_log
      if (recentLog) {
        setMapCenter({ lat: Number(recentLog.latitude), lng: Number(recentLog.longitude) })
      }
    }
  }, [selectedMamorio, mamoriosOrDevices])

  const infoWindowOptions = {
    pixelOffset: size,
  }

  const updateOffsetSize = () => setSize(new window.google.maps.Size(0, 0))

  const recentPosition = useCallback((mamorio: IMamorio): Position | null | undefined => {
    const recentLog = mamorio.recent_log
    if (!recentLog) return null

    const position = {
      lat: Number(recentLog.latitude),
      lng: Number(recentLog.longitude),
    }
    return position
  }, [])

  const recentDetectedAt = useCallback((mamorio: IMamorio): string => {
    const recentLog = mamorio.recent_log
    if (!recentLog) return ''
    return getFromNowAsText(recentLog.at)
  }, [])

  const mamorioPositionGroups = groupMamoriosByPosition(
    mamoriosOrDevices
      .filter((mamorio) => !showInfoWindow || selectedMamorio?.id === mamorio.id) // InfoWindow表示時は選択したマーカーのみ、非表示時は全て表示
      .filter((mamorio) => recentPosition(mamorio) != null),
    recentPosition,
  )

  const clusters = (clusterer: Clusterer): JSX.Element[] => mamorioPositionGroups
    .map((positionGroup) => {
      if (positionGroup.mamorios.length > 1) {
        return (
          <OverlappingMamoriosMarker
            key={`overlapping-mamorios-${positionGroup.positionKey}`}
            clusterer={clusterer}
            mamorios={positionGroup.mamorios}
            position={positionGroup.position}
            isOpen={openOverlappingPositionKey === positionGroup.positionKey}
            onOpen={() => {
              setSelectedMamorio(null)
              setShowInfoWindow(false)
              setOpenOverlappingPositionKey(positionGroup.positionKey)
            }}
            onClose={() => setOpenOverlappingPositionKey(null)}
            onMamorioSelect={(mamorio) => {
              setOpenOverlappingPositionKey(null)
              setSelectedMamorio(mamorio)
              setShowInfoWindow(true)
            }}
            infoWindowOptions={infoWindowOptions}
            recentDetectedAt={recentDetectedAt}
            showMemo={false}
          />
        )
      }

      const mamorio = positionGroup.mamorios[0]!
      return (
        <MamorioMarker
          key={mamorio.id}
          mamorio={mamorio}
          clusterer={clusterer}
          isSelected={selectedMamorio?.id === mamorio.id && showInfoWindow} // InfoWindow表示時のみ選択状態を表示
          onMarkerClick={() => {
            setOpenOverlappingPositionKey(null)
            if (selectedMamorio?.id === mamorio.id) {
              // 同じマーカーをクリック: InfoWindowをトグル
              setShowInfoWindow(!showInfoWindow)
            } else {
              // 異なるマーカーをクリック: 選択を変更してInfoWindowを表示
              setSelectedMamorio(mamorio)
              setShowInfoWindow(true)
            }
          }}
          onInfoWindowClose={() => {
            // InfoWindowを閉じる: 選択状態は維持、全マーカーを表示
            setShowInfoWindow(false)
            setOpenOverlappingPositionKey(null)
          }}
          infoWindowOptions={infoWindowOptions}
          recentPosition={recentPosition}
          recentDetectedAt={recentDetectedAt}
          showMemo={false}
        />
      )
    })

  const renderMap = () => (
    <GoogleMap
      zoom={zoom}
      center={mapCenter}
      mapContainerStyle={containerStyle}
      onLoad={() => updateOffsetSize()}
    >
      <MarkerClusterer
        averageCenter
        calculator={calculateMamorioClusterIcon}
        enableRetinaIcons
        gridSize={60}
        maxZoom={GOOGLE_MAP_CLUSTER_MAX_ZOOM}
        styles={clusterStyles}
      >
        {(clusterer) => (<>{clusters(clusterer)}</>)}
      </MarkerClusterer>
    </GoogleMap>
  )

  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>
  }

  return isLoaded ? renderMap() : null
}

export default GoogleMapWrapperForMamoriosView
